import React, {Fragment, useCallback, useEffect, useState} from "react";
import {Link, useLocation} from "react-router-dom";
import {toast} from "react-toastify";
import Select from "react-select";
import moment from "moment";
import {Badge, Card, CardBody, Col, Container, Form, FormGroup, Input, Row} from "reactstrap";
import Breadcrumbs from "../../layout/breadcrumb";
import CommonDataTable from "../../share-components/table/commonDataTable";
import DateRangePickerPrimary from "../../share-components/ui/DateRangePickerPrimary";
import {exportActivityLogApi, getActivityLogApi, getActivityLogOptionsApi} from "../../services/tagsAutomationService";
import {BASE, errorMessage, RequirePermission, TagBadge, useCan} from "./common";
import "./tagsAutomation.scss";

const RESULT_COLOR = {
    written: 'success', completed: 'success',
    failed: 'danger', unresolved: 'danger', completed_with_errors: 'danger',
    no_change: 'light', running: 'warning',
};

const selectStyles = {
    control: (base) => ({...base, minHeight: 50, border: "none"}),
};


const change = (r) => {
    // The US push sends the full tag list without reading the US store first, so there is no
    // "before" to compare against: show what was sent rather than every tag as added.
    if (r.store === 'us') return {cls: 'badge-light', text: `Tags sent to US (${r.tags_after.length})`, summary: ''};
    const a = r.tags_added.length;
    const d = r.tags_removed.length;
    if (a && d) return {cls: 'badge-light-primary', text: `+${a} / −${d}`, summary: [...r.tags_added, ...r.tags_removed].slice(0, 3).join(', ')};
    if (a) return {cls: 'badge-light-primary', text: `+${a} added`, summary: r.tags_added.join(', ')};
    if (d) return {cls: 'badge-light-danger', text: `−${d} removed`, summary: d === 1 ? r.tags_removed[0] : d === 2 ? r.tags_removed.join(', ') : `${r.tags_removed[0]}, ${r.tags_removed[1]} and ${d - 2} more`};
    return {cls: 'badge-light', text: 'No change', summary: ''};
};

const shopifyText = (r) => {
    const store = r.store === 'us' ? ' (US store)' : '';
    if (r.result === 'written') return 'OK' + store;
    if (r.result === 'no_change') return 'No write needed' + store;
    return (r.message || r.result_label) + store;
};

const isFailed = (r) => ['failed', 'unresolved'].includes(r.result);

const ResultBadge = ({r}) => <Badge color={RESULT_COLOR[r.result] || 'light'}>{r.result_label}</Badge>;

/** The expanded row: why the product changed and what Shopify holds now. */
const Detail = ({data: r}) => {
    const can = useCan();
    const [showAll, setShowAll] = useState(false);
    const firstWorkflow = r.workflows[0];
    const line = (label, value) => (
        <Row className="mb-2">
            <Col md={2} className="f-12 text-muted">{label}</Col>
            <Col md={10} style={{overflowWrap: 'anywhere'}}>{value}</Col>
        </Row>
    );
    return (
        <div className="p-3 ta-soft">
            {line('Trigger', r.trigger_detail)}
            {r.workflows.length === 0 && line('Workflow', 'No workflow changed this product (a tag written directly, e.g. Size_ or Back in Stock)')}
            {r.workflows.map(w => (
                <Fragment key={w.id}>
                    {line('Workflow', <>{can('Update Workflow') ? <Link to={`${BASE}/workflows/${w.id}/edit`}>{w.name}</Link> : w.name} <span className="text-muted">#{w.id}</span></>)}
                    {line('Matched on', w.reason)}
                </Fragment>
            ))}
            {r.tags_removed.length > 0 && line(`Removed (${r.tags_removed.length})`, <div className="d-flex flex-wrap" style={{gap: 6}}>{r.tags_removed.map(t => <TagBadge key={t} tag={t} kind="remove"/>)}</div>)}
            {r.store !== 'us' && r.tags_added.length > 0 && line(`Added (${r.tags_added.length})`, <div className="d-flex flex-wrap" style={{gap: 6}}>{r.tags_added.map(t => <TagBadge key={t} tag={t} kind="add"/>)}</div>)}
            {line(`Tags now (${r.tags_after.length})`, <>
                {showAll && <div className="d-flex flex-wrap mb-2" style={{gap: 6}}>{r.tags_after.map(t => <TagBadge key={t} tag={t}/>)}</div>}
                {r.tags_after.length > 0 && <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setShowAll(!showAll)}>{showAll ? 'Hide tags' : `Show all ${r.tags_after.length} tags`}</button>}
            </>)}
            {line('Shopify', <span className={isFailed(r) ? 'text-danger' : ''}>{shopifyText(r)}</span>)}
            <div className="d-flex justify-content-end flex-wrap border-top pt-3 mt-2" style={{gap: 8}}>
                {r.product && <Link className="btn btn-sm btn-outline-primary" to={`${process.env.PUBLIC_URL}/product/${r.product.id}/edit`}>Open product</Link>}
                {r.product?.shopify_url && <a className="btn btn-sm btn-outline-primary" href={r.product.shopify_url} target="_blank" rel="noopener noreferrer">View in Shopify</a>}
                {r.product && firstWorkflow && can('Workflows') && <Link className="btn btn-sm btn-outline-primary" to={`${BASE}/workflows/${firstWorkflow.id}/test?product_id=${r.product.id}`}>Re-test</Link>}
            </div>
        </div>
    );
};

/**
 * GOD-TASK-197 Tags Automation › Activity Log (prototype screen 5).
 */
const ActivityLog = () => {
    const can = useCan();
    const dataMenu = [
        can('Workflows') ? {type: 1, text: 'Tags Automation', link: '/tags-automation/workflows'} : {type: 0, text: 'Tags Automation'},
        {type: 0, text: 'Activity Log'},
    ];
    const location = useLocation();
    const [filters, setFilters] = useState(() => {
        const q = new URLSearchParams(location.search);
        return {product: '', workflow_id: q.get('workflow_id') || '', trigger: '', result: q.get('result') || '', date_from: '', date_to: ''};
    });
    const [productText, setProductText] = useState('');
    const [dateRange, setDateRange] = useState([null, null]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [resetPage, setResetPage] = useState(false);
    const [list, setList] = useState({data: [], total: 0});
    const [options, setOptions] = useState({workflows: [], triggers: [], results: []});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [exporting, setExporting] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        getActivityLogApi({...filters, page, page_size: pageSize})
            .then(res => {
                setList(res);
                setError(null);
            })
            .catch(e => setError(errorMessage(e)))
            .finally(() => setLoading(false));
    }, [filters, page, pageSize]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        getActivityLogOptionsApi().then(setOptions).catch(() => {});
    }, []);

    const toFirstPage = () => {
        setPage(1);
        setResetPage(r => !r);
    };

    useEffect(() => {
        const t = setTimeout(() => {
            setFilters(f => (f.product === productText ? f : {...f, product: productText}));
            toFirstPage();
        }, 400);
        return () => clearTimeout(t);
    }, [productText]);

    const setFilter = (patch) => {
        setFilters(f => ({...f, ...patch}));
        toFirstPage();
    };

    const exportCsv = () => {
        setExporting(true);
        exportActivityLogApi(filters)
            .then(blob => {
                const url = window.URL.createObjectURL(new Blob([blob], {type: 'text/csv'}));
                const a = document.createElement('a');
                a.href = url;
                a.download = `tags-automation-activity-${new Date().toISOString().slice(0, 10)}.csv`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
            })
            .catch(e => toast.error(errorMessage(e), {position: toast.POSITION.TOP_RIGHT}))
            .finally(() => setExporting(false));
    };

    const optionFor = (opts, value) => opts.find(o => String(o.value) === String(value)) || null;
    const select = (placeholder, opts, key) => (
        <Select placeholder={placeholder} className="select2-filter" isClearable styles={selectStyles}
                options={opts} value={optionFor(opts, filters[key])}
                onChange={o => setFilter({[key]: o ? o.value : ''})} components={{IndicatorSeparator: () => null}}/>
    );

    const columns = [
        {name: 'Date and time', selector: r => <span className="f-12 text-muted">{r.date}</span>, width: '170px'},
        {
            name: 'Product',
            selector: r => r.is_summary ? '—' : <div><b>{r.product ? r.product.code : '—'}</b><div className="f-12 text-muted">{r.product?.colour || ''}</div></div>,
            width: '170px',
        },
        {
            name: 'Workflow',
            selector: r => r.is_summary ? <b>{r.summary.split(' · ')[0]}</b> : (r.workflows.map(w => w.name).join(', ') || '—'),
            width: '240px',
            wrap: true,
        },
        {
            name: 'Change',
            selector: r => {
                if (r.is_summary) return <span className="text-muted" style={{whiteSpace: 'normal'}}>{r.summary.split(' · ').slice(1).join(' · ')}</span>;
                const c = change(r);
                return (
                    <div className="d-flex align-items-center" style={{gap: 10, minWidth: 0}}>
                        <span className={`badge ta-badge ${c.cls}`}>{c.text}</span>
                        <span className="f-12 text-muted text-truncate">{c.summary}</span>
                    </div>
                );
            },
            grow: 2,
            // Run summaries are long; let them wrap instead of widening the whole table.
            wrap: true,
        },
        {name: 'Trigger', selector: r => <span className="badge ta-badge badge-light-info">{r.trigger}</span>, width: '150px'},
        {name: 'Result', selector: r => <ResultBadge r={r}/>, width: '210px'},
    ];

    return (
        <Fragment>
            <Breadcrumbs title="Activity Log" data={dataMenu}/>
            <Container fluid={true} className="product-wrapper">
                <div className="product-grid">
                    <div className="feature-products">
                        <Card>
                            <div className="d-flex justify-content-end flex-wrap p-3" style={{gap: 8}}>
                                {can('Workflows') && <Link className="btn btn-sm btn-outline-primary" to={`${BASE}/workflows`}><i className="fa fa-arrow-left"/> Workflows</Link>}
                                <button className="btn btn-sm btn-primary" type="button" disabled={exporting} onClick={exportCsv}>
                                    <i className="fa fa-download"/> {exporting ? 'Exporting…' : 'Export CSV'}
                                </button>
                            </div>
                        </Card>
                    </div>

                    {error && <div className="alert alert-danger">Could not load the activity log: {error}</div>}

                    <Row>
                        <Col md={3}>
                            <div className="dashboard-datepicker">
                                <DateRangePickerPrimary
                                    dateRange={dateRange}
                                    cleanable
                                    placeholder="Date (DD/MM/YYYY)"
                                    dateRangeChange={range => {
                                        const r = range || [null, null];
                                        setDateRange(r);
                                        setFilter({
                                            date_from: r[0] ? moment(r[0]).format('YYYY-MM-DD') : '',
                                            date_to: r[1] ? moment(r[1]).format('YYYY-MM-DD') : '',
                                        });
                                    }}
                                />
                            </div>
                        </Col>
                        <Col md={3}>
                            <Form onSubmit={e => e.preventDefault()}>
                                <FormGroup className="m-0">
                                    <Input bsSize="lg" type="text" placeholder="Product style code" value={productText} onChange={e => setProductText(e.target.value)}/>
                                </FormGroup>
                            </Form>
                        </Col>
                        <Col md={2}>{select('Workflow', options.workflows, 'workflow_id')}</Col>
                        <Col md={2}>{select('Trigger', options.triggers, 'trigger')}</Col>
                        <Col md={2}>{select('Result', options.results, 'result')}</Col>
                    </Row>

                    <Row>
                        <Col className="mt-3" sm="12">
                            <Card>
                                <CardBody>
                                    <div className="table-responsive product-table">
                                        <CommonDataTable
                                            headerColumns={columns}
                                            gridData={list.data}
                                            noAction
                                            progressPending={loading}
                                            paginationServer
                                            paginationTotalRows={list.total}
                                            paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                            paginationPerPage={pageSize}
                                            paginationDefaultPage={1}
                                            paginationResetDefaultPage={resetPage}
                                            onChangePage={setPage}
                                            onChangeRowsPerPage={size => {
                                                setPageSize(size);
                                                toFirstPage();
                                            }}
                                            expandableRows
                                            expandableRowDisabled={r => r.is_summary}
                                            expandableRowsComponent={Detail}
                                            conditionalRowStyles={[{when: r => isFailed(r), style: {backgroundColor: '#fff8f8'}}]}
                                            noDataComponent={<div className="p-4 text-center">No activity matches these filters.</div>}
                                        />
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </div>
            </Container>
        </Fragment>
    );
};

const ActivityLogPage = () => <RequirePermission name="Activity Log"><ActivityLog/></RequirePermission>;

export default ActivityLogPage;
